// TelcoTrack – zobrazení zadaných údajů (délka, šířka, materiál) u řádku výkazu práce

import type { WorkReportRow } from '../../core/models/object.model';
import { getPolygonIdForFilter } from './activity-summary.utils';
import { matchesMultiValueFilter } from './filter-multi.util';
import { resolveAttachmentPhaseLabel } from './object.utils';
import {
  getExcavationMaterialName,
  getExcavationSubtypeName,
  REMOVAL_BEYOND_SCOPE_NAMES,
  ZAKOPANY_OBNOVA_NAMES,
  ZAKOPANY_WORK_BEYOND_SCOPE_NAMES,
  WORK_REPORT_PAYMENT_STATUS,
  WORK_REPORT_PAYMENT_STATUS_LABELS,
  normalizeWorkReportPaymentStatus,
  WORK_REPORT_PRICE_LIST,
  type PriceListUnit,
  type WorkReportPaymentStatus,
  type WorkReportPriceListItem,
  formatDeepeningWorkReportActivityName,
  formatWorkReportDeepeningSpecText,
  isDeepeningWorkReportActivityName,
  WORK_REPORT_DEEPENING_CATALOG_ACTIVITY_NAME,
  workReportLineAmount,
  type WorkReportMoneyView,
  formatSpecialRemovalRowsForDisplay,
  formatWorkReportLineItemDetail,
  isObnovaWorkReportActivityName,
  isPokladkaHdpeMultiductWorkReportActivityName,
  resolveWorkReportObnovaM2,
  resolveWorkReportPokladkaHdpeLengthM,
  resolveWorkReportRouteLengthM,
  UKONCENI_V_RD_LABELS,
  type UkonceniVRd,
} from '@core/shared-bridge';

const LABELS: Record<string, string> = {
  excavationLengthM: 'Délka výkopu',
  zakopanyLengthM: 'Délka (zakopaný)',
  pokladkaHdpeLengthM: 'Délka použitého materiálu',
  excavationMaterial: 'Materiál',
  excavationMaterialSubtype: 'Podtyp povrchu',
  removalBeyondScope: 'Práce nad rámec',
  removalBeyondScopeQuantity: 'Množství (nad rámec)',
  removalBeyondScopeWidthM: 'Šířka (nad rámec)',
  removalBeyondScopeDepthM: 'Hloubka (nad rámec)',
  removalBeyondScopeLengthM: 'Délka bourání (nad rámec)',
  excavationDeepeningCm: 'Prohloubení',
  workBeyondScopeZakopany: 'Práce nad rámec (zakopaný)',
  workBeyondScopeZakopanyM2: 'Plocha nad rámec (m²)',
  workBeyondScopeZakopanyWidthM: 'Šířka nad rámec (zakopaný)',
  restorationZakopany: 'Obnova',
  restorationZakopanyM2: 'Plocha obnova (m²)',
  restorationZakopanyWidthM: 'Šířka obnova',
  zakopanySurfaces: 'Finální (povrchy)',
  specialRemovalRows: 'Bourání / rozvoz',
  pripojovanyZafukSekundarnichOkPocet: 'Počet záfuků sekundárních OK',
  ukonceniVRd: 'Ukončení v RD',
  ukonceniVRdOther: 'Ukončení v RD — upřesnění',
  pripojovanyZafouknutoZRozvadecName: 'Zafouknuto z rozvaděče',
};

/** Pořadí sloupců specifikace ve výkazu — export Excel (české popisky viz `getWorkReportSpecExportHeaders`). */
export const WORK_REPORT_SPEC_EXPORT_KEY_ORDER = Object.keys(LABELS);

export function getWorkReportSpecExportHeaders(): string[] {
  return WORK_REPORT_SPEC_EXPORT_KEY_ORDER.map((k) => LABELS[k] ?? k);
}

function formatValue(key: string, val: unknown): string {
  if (val == null || val === '') return '';
  if (typeof val === 'object') return '';
  if (typeof val === 'number' && Number.isFinite(val)) {
    if (key === 'excavationDeepeningCm') {
      if (typeof val === 'string') return val;
      return `${val} cm`;
    }
    if (key.endsWith('M2') || key.includes('ZakopanyM2')) return `${val} m²`;
    if (key.endsWith('M') || key.endsWith('WidthM') || key.endsWith('DepthM')) return `${val} m`;
    return String(val);
  }
  return String(val);
}

function parsePositiveNumber(raw: unknown): number | undefined {
  if (raw == null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function isPokladkaHdpeMultiductWorkReportRow(row: WorkReportRow): boolean {
  return isPokladkaHdpeMultiductWorkReportActivityName(row.activityName ?? '');
}

function workReportLineItemDetailInput(row: WorkReportRow) {
  return {
    activityName: row.activityName,
    excavationLengthM: row.excavationLengthM,
    excavationMaterial: row.excavationMaterial,
    excavationMaterialSubtype: row.excavationMaterialSubtype,
    metadata: row.metadata as Record<string, unknown> | undefined,
  };
}

function formatDisplayValue(key: string, val: unknown): string {
  if (val == null || val === '') return '';
  if (typeof val === 'object') return '';
  const s = String(val);

  switch (key) {
    case 'excavationMaterial':
      return getExcavationMaterialName(s);
    case 'excavationMaterialSubtype':
      return getExcavationSubtypeName(s);
    case 'removalBeyondScope':
      return REMOVAL_BEYOND_SCOPE_NAMES[s] ?? s;
    case 'workBeyondScopeZakopany':
      return ZAKOPANY_WORK_BEYOND_SCOPE_NAMES[s] ?? s;
    case 'restorationZakopany':
      return ZAKOPANY_OBNOVA_NAMES[s] ?? s;
    case 'ukonceniVRd':
      return UKONCENI_V_RD_LABELS[s as UkonceniVRd] ?? s;
    default:
      return formatValue(key, val);
  }
}

/**
 * Sloučená pole specifikace výkopu/zadání (stejný zdroj jako text pod řádkem výkazu).
 * Klíče odpovídají `WORK_REPORT_SPEC_EXPORT_KEY_ORDER` + případné další z `metadata`.
 */
export function collectWorkReportFlatSpec(row: WorkReportRow): Record<string, unknown> {
  const flat: Record<string, unknown> = {};
  const meta = row.metadata;
  const snap =
    meta && typeof meta === 'object' && meta !== null && 'excavationSnapshot' in meta
      ? (meta as Record<string, unknown>)['excavationSnapshot']
      : undefined;

  if (snap && typeof snap === 'object' && !Array.isArray(snap)) {
    Object.assign(flat, snap as Record<string, unknown>);
    const segMat = row['excavationMaterial'];
    const segSub = row['excavationMaterialSubtype'];
    if (typeof segMat === 'string' && segMat.trim() !== '') {
      flat['excavationMaterial'] = segMat.trim();
    }
    if (typeof segSub === 'string' && segSub.trim() !== '') {
      flat['excavationMaterialSubtype'] = segSub.trim();
    }
    const deepeningText = formatWorkReportDeepeningSpecText(
      (snap as Record<string, unknown>)['excavationSurfaces'],
      (snap as Record<string, unknown>)['excavationDeepeningCm'],
    );
    if (deepeningText) {
      flat['excavationDeepeningCm'] = deepeningText;
    }
    delete flat['excavationSurfaces'];
    const zsRaw = flat['zakopanySurfaces'];
    if (Array.isArray(zsRaw) && zsRaw.length > 0) {
      flat['zakopanySurfaces'] = zsRaw
        .map((surf: unknown) => {
          const r = surf as Record<string, unknown>;
          const code = typeof r['code'] === 'string' ? r['code'].trim() : '';
          if (!code) return '';
          const name =
            ZAKOPANY_WORK_BEYOND_SCOPE_NAMES[code as keyof typeof ZAKOPANY_WORK_BEYOND_SCOPE_NAMES] ??
            ZAKOPANY_OBNOVA_NAMES[code as keyof typeof ZAKOPANY_OBNOVA_NAMES] ??
            code;
          const rLen = r['lengthM'];
          const len =
            typeof rLen === 'number' && Number.isFinite(rLen) && rLen > 0 ? `${rLen} m` : 'trasa';
          const rW = r['widthM'];
          const wid =
            typeof rW === 'number' && Number.isFinite(rW) && rW > 0 ? `${rW} m` : '';
          const rM2 = r['m2'];
          const m2 =
            typeof rM2 === 'number' && Number.isFinite(rM2) && rM2 > 0 ? ` → ${rM2} m²` : '';
          if (!wid) return '';
          return `${name} (${len} × šířka ${wid}${m2})`;
        })
        .filter(Boolean)
        .join(' · ');
    }
    if (meta && typeof meta === 'object' && meta !== null) {
      const top = meta as Record<string, unknown>;
      if (top['pokladkaHdpeLengthM'] != null) flat['pokladkaHdpeLengthM'] = top['pokladkaHdpeLengthM'];
    }
  } else {
    if (row['excavationLengthM'] != null) flat['excavationLengthM'] = row['excavationLengthM'];
    if (row['excavationMaterial']) flat['excavationMaterial'] = row['excavationMaterial'];
    if (row['excavationMaterialSubtype']) flat['excavationMaterialSubtype'] = row['excavationMaterialSubtype'];
    if (meta && typeof meta === 'object' && meta !== null) {
      for (const [k, v] of Object.entries(meta as Record<string, unknown>)) {
        if (k === 'excavationSnapshot') continue;
        if (k.startsWith('workShare')) continue;
        if (v != null && v !== '' && flat[k] === undefined) flat[k] = v;
      }
    }
  }
  const detailInput = workReportLineItemDetailInput(row);
  if (isPokladkaHdpeMultiductWorkReportRow(row)) {
    const materialLen = resolveWorkReportPokladkaHdpeLengthM(detailInput);
    if (materialLen != null) flat['pokladkaHdpeLengthM'] = materialLen;
    delete flat['excavationLengthM'];
  } else {
    const routeLen = resolveWorkReportRouteLengthM(detailInput);
    if (routeLen != null) flat['excavationLengthM'] = routeLen;
    else delete flat['excavationLengthM'];
  }
  const specialRaw = flat['specialRemovalRows'];
  if (Array.isArray(specialRaw)) {
    const specialFormatted = formatSpecialRemovalRowsForDisplay(specialRaw);
    if (specialFormatted) flat['specialRemovalRows'] = specialFormatted;
    else delete flat['specialRemovalRows'];
  }
  return flat;
}

/** Název činnosti — u prohloubení nahradí „o 10 cm“ hodnotou ze snapshotu. */
/** Ruční záznam ve výkazu (`phaseTo = MANUAL`) — často bez `polygon_id`. */
export function isWorkReportManualRow(row: { phaseTo?: string; phase_to?: string }): boolean {
  return row.phaseTo === 'MANUAL' || row.phase_to === 'MANUAL';
}

/**
 * Polygon filtr ve výkazu: ruční řádky bez `polygon_id` zůstanou viditelné
 * (akvizice přes „Ostatní“); ruční s polygonem se filtrují jako ostatní řádky.
 */
export function workReportRowMatchesPolygonFilter(
  row: { phaseTo?: string; phase_to?: string; polygonId?: string | null; polygon_id?: string | null },
  polygonIds: string[],
): boolean {
  if (polygonIds.length === 0) return true;
  if (isWorkReportManualRow(row)) {
    const pid = getPolygonIdForFilter(row);
    if (!pid) return true;
  }
  return matchesMultiValueFilter(getPolygonIdForFilter(row), polygonIds);
}

export function resolveWorkReportActivityDisplayName(row: WorkReportRow): string {
  const stored = (row.activityName ?? '—').trim() || '—';
  const snap =
    row.metadata && typeof row.metadata === 'object' && 'excavationSnapshot' in row.metadata
      ? (row.metadata as Record<string, unknown>)['excavationSnapshot']
      : undefined;
  if (!snap || typeof snap !== 'object' || Array.isArray(snap)) return stored;
  const rec = snap as Record<string, unknown>;
  return formatDeepeningWorkReportActivityName(
    stored,
    rec['excavationSurfaces'],
    rec['excavationDeepeningCm'],
  );
}

/** Kdo naposledy změnil stav proplacení (manažerský PATCH). */
export function formatWorkReportPaymentChangedBy(row: WorkReportRow): string | null {
  const by = row.paymentStatusChangedBy?.trim();
  return by || null;
}

/** Kdy naposledy změněn stav proplacení. */
export function formatWorkReportPaymentChangedAt(row: WorkReportRow): string | null {
  const atRaw = row.paymentStatusChangedAt?.trim();
  if (!atRaw) return null;
  const d = new Date(atRaw);
  return Number.isNaN(d.getTime())
    ? atRaw
    : new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'short', timeStyle: 'short' }).format(d);
}

/** Pod badge stavu proplacení: kdo a kdy v jednom řetězci (např. export). */
export function formatWorkReportPaymentAuditCaption(row: WorkReportRow): string | null {
  const by = formatWorkReportPaymentChangedBy(row);
  const when = formatWorkReportPaymentChangedAt(row);
  if (!by && !when) return null;
  const parts: string[] = [];
  if (by) parts.push(by);
  if (when) parts.push(when);
  return parts.join(' · ');
}

/**
 * Hodnoty specifikace v pevném pořadí pro Excel (prázdný řetězec = nevyplněno).
 * Metrové hodnoty (délka, šířka, hloubka) se vrací jako číslo, aby Excel
 * zobrazil desetinnou čárku dle národního prostředí — bez přípony „m".
 */
export function buildWorkReportSpecCellsForExport(row: WorkReportRow): (string | number)[] {
  const flat = collectWorkReportFlatSpec(row);
  return WORK_REPORT_SPEC_EXPORT_KEY_ORDER.map((key) => {
    const v = flat[key];
    if (v == null || v === '') return '';
    // Metrové klíče: vrátit raw číslo — Excel použije desetinnou čárku dle locale
    if (
      typeof v === 'number' &&
      Number.isFinite(v) &&
      (key.endsWith('M') || key.endsWith('WidthM') || key.endsWith('DepthM'))
    ) {
      return v;
    }
    return formatDisplayValue(key, v) || '';
  });
}

/**
 * Textový řetězec pro zobrazení pod řádkem výkazu (zadání z mapy při změně fáze).
 * Základ z `metadata.excavationSnapshot` (kompletní zadání); délka/materiál/podtyp řádku z DB sloupců,
 * pokud jsou vyplněné (více povrchů = jeden řádek výkazu na úsek).
 */
export function formatWorkReportExcavationDetail(row: WorkReportRow): string {
  const excavation = formatWorkReportLineItemDetail(workReportLineItemDetailInput(row)) ?? '';
  const splitNote = formatWorkShareSplitNote(row);
  if (!splitNote) return excavation;
  if (!excavation) return splitNote;
  return `${excavation} · ${splitNote}`;
}

/**
 * Pokud řádek vznikl rozdělením výkazu mezi více osob (metadata z backendu po spolupráci).
 */
export function formatWorkShareSplitNote(row: WorkReportRow): string | null {
  const meta = row.metadata;
  if (!meta || typeof meta !== 'object') return null;
  const m = meta as Record<string, unknown>;
  const count = Number(m['workShareCount']);
  if (!Number.isFinite(count) || count < 2) return null;
  const idx = Number(m['workShareIndex']);
  const part = Number.isFinite(idx) ? idx + 1 : null;
  if (part != null) {
    return `Podíl z částky rozdělené mezi ${count} osobami (${part}. z ${count})`;
  }
  return `Podíl z částky rozdělené mezi ${count} osobami`;
}

/**
 * Jeden list Excelu „Výkaz práce“ — stejné sloupce jako export z detailu měsíce / období uživatele.
 */
export function buildWorkReportSheetExportAoA(
  rows: WorkReportRow[],
  formatDateCell: (createdAt: string | Date) => string,
  options?: { includeOrigin?: boolean },
): (string | number)[][] {
  const specHeaders = getWorkReportSpecExportHeaders();
  const includeOrigin = options?.includeOrigin !== false;
  const headers = [
    'Název činnosti',
    ...(includeOrigin ? (['Původ'] as const) : []),
    'Cena',
    'Stav proplacení',
    'Proplacení',
    'Datum',
    'Fáze z',
    'Fáze na',
    'Poznámka',
    'Podíl (spolupráce)',
    ...specHeaders,
  ];
  const out: (string | number)[][] = [headers];
  for (const row of rows) {
    let origin = '—';
    if (includeOrigin) {
      if (row.phaseTo === 'MANUAL') origin = 'Ruční záznam';
      else if (row.objectId) origin = 'Objekt';
    }
    const price = row.price ?? '';
    const fromCode = (row.phaseFrom ?? '').trim();
    const toCode = (row.phaseTo ?? '').trim();
    const phaseFrom =
      fromCode !== ''
        ? resolveAttachmentPhaseLabel(fromCode, row.objectType) || fromCode
        : '';
    const phaseTo =
      toCode !== ''
        ? resolveAttachmentPhaseLabel(toCode, row.objectType) || toCode
        : '';
    const note = (row.description ?? '').trim();
    const share = formatWorkShareSplitNote(row) ?? '';
    const pay = normalizeWorkReportPaymentStatus(row.paymentStatus);
    const payLabel = WORK_REPORT_PAYMENT_STATUS_LABELS[pay];
    const payBy = formatWorkReportPaymentChangedBy(row) ?? '';
    const payAtCell = formatWorkReportPaymentChangedAt(row) ?? '';
    const payAudit =
      payBy && payAtCell ? `${payBy}\n${payAtCell}` : payBy || payAtCell || '';
    out.push([
      resolveWorkReportActivityDisplayName(row),
      ...(includeOrigin ? [origin] : []),
      price,
      payLabel,
      payAudit,
      formatDateCell(row.createdAt),
      phaseFrom,
      phaseTo,
      note,
      share,
      ...buildWorkReportSpecCellsForExport(row),
    ]);
  }
  return out;
}

// =============================================================================
// Zobrazení řádků výkazu: detail (1:1 s DB) vs agregace stejné položky (`activityName`)
// =============================================================================

export type WorkReportDisplayMode = 'detail' | 'aggregated';

/** Jedna vizuální řádka tabulky — buď jeden DB řádek, nebo agregát více řádků. */
export interface WorkReportDisplayLine {
  displayId: string;
  isAggregate: boolean;
  aggregateKind: 'activity_group' | null;
  sourceRows: WorkReportRow[];
  title: string;
  /** Souhrn: součet m / ks / … (např. „125 m“, „3 ks“). */
  quantityLabel?: string;
  /** Volitelné podnadpisy pod názvem (detailní řádky; agregát je bez rozpisu). */
  detailLines: string[];
  totalPrice: number;
  /** Pro agregáty: rozsah datumů vytvoření záznamů. */
  dateLabel: string;
  phaseToRep: string;
}

/** Souhrn: prohloubení = jedna položka ceníku (vždy „o 10 cm“). Detail: skutečná cm. */
function workReportActivityCatalogName(row: WorkReportRow): string {
  const stored = (row.activityName ?? '').trim();
  if (isDeepeningWorkReportActivityName(stored)) return WORK_REPORT_DEEPENING_CATALOG_ACTIVITY_NAME;
  return stored;
}

/** Klíč pro seskupení řádků se stejným názvem činnosti; řádky bez názvu se neslučují. */
export function workReportActivityGroupKey(row: WorkReportRow): string {
  const n = workReportActivityCatalogName(row);
  return n !== '' ? `name:${n}` : `id:${row.id}`;
}

export function buildWorkReportDisplayLines(
  rows: WorkReportRow[],
  mode: WorkReportDisplayMode,
  priceView: WorkReportMoneyView = 'worker',
): WorkReportDisplayLine[] {
  if (mode === 'detail') {
    return rows.map((row) => buildSingleDisplayLine(row, priceView));
  }
  return buildAggregatedDisplayLines(rows, priceView);
}

/** Zachová pořadí prvního výskytu skupiny podle vstupního pole řádků. */
function buildAggregatedDisplayLines(
  rows: WorkReportRow[],
  priceView: WorkReportMoneyView,
): WorkReportDisplayLine[] {
  const groups = new Map<string, WorkReportRow[]>();
  const keyOrder: string[] = [];
  for (const r of rows) {
    const k = workReportActivityGroupKey(r);
    let arr = groups.get(k);
    if (!arr) {
      arr = [];
      groups.set(k, arr);
      keyOrder.push(k);
    }
    arr.push(r);
  }
  const out: WorkReportDisplayLine[] = [];
  for (const k of keyOrder) {
    out.push(buildSameActivityAggregateLine(groups.get(k)!, priceView));
  }
  return out;
}

let priceListByActivityName: Map<string, WorkReportPriceListItem> | null = null;

function getPriceListItemForActivity(activityName: string): WorkReportPriceListItem | null {
  const name = (activityName ?? '').trim();
  if (!name) return null;
  if (!priceListByActivityName) {
    priceListByActivityName = new Map(
      WORK_REPORT_PRICE_LIST.map((item) => [item.activityName, item] as const),
    );
  }
  const direct = priceListByActivityName.get(name);
  if (direct) return direct;
  if (isDeepeningWorkReportActivityName(name)) {
    return priceListByActivityName.get(WORK_REPORT_DEEPENING_CATALOG_ACTIVITY_NAME) ?? null;
  }
  const base = name.replace(/\s*[–-]\s*\d+(\.\d+)?\s*m\s*$/i, '').trim();
  return base ? priceListByActivityName.get(base) ?? null : null;
}

function roundWorkReportQuantity(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function workReportRowQuantityFromPrice(row: WorkReportRow, unitPrice: number): number {
  const price = Number(row.price);
  if (!Number.isFinite(price) || price <= 0) return 0;
  if (unitPrice > 0) {
    const q = price / unitPrice;
    if (Number.isFinite(q) && q > 0) return roundWorkReportQuantity(q);
  }
  return 0;
}

/** Množství z délky / specifikace řádku (spolehlivější než cena při override cen polygonu). */
function workReportRowPhysicalQuantity(row: WorkReportRow, unit: PriceListUnit): number | null {
  if (unit === 'm') {
    if (isDeepeningWorkReportActivityName(row.activityName ?? '')) {
      const item = getPriceListItemForActivity(row.activityName ?? '');
      if (item?.unit === 'm' && item.pricePerUnit > 0) {
        const q = workReportRowQuantityFromPrice(row, item.pricePerUnit);
        if (q > 0) return q;
      }
    }
    if (isPokladkaHdpeMultiductWorkReportRow(row)) {
      const materialLen = resolveWorkReportPokladkaHdpeLengthM(workReportLineItemDetailInput(row));
      if (materialLen != null) return materialLen;
      const item = getPriceListItemForActivity(row.activityName ?? '');
      if (item?.unit === 'm' && item.pricePerUnit > 0) {
        const q = workReportRowQuantityFromPrice(row, item.pricePerUnit);
        if (q > 0) return q;
      }
    }
    const routeLen = resolveWorkReportRouteLengthM(workReportLineItemDetailInput(row));
    if (routeLen != null) return routeLen;
    const flat = collectWorkReportFlatSpec(row);
    for (const key of ['pokladkaHdpeLengthM', 'zakopanyLengthM', 'removalBeyondScopeLengthM'] as const) {
      const v = Number(flat[key]);
      if (Number.isFinite(v) && v > 0) return v;
    }
  }
  if (unit === 'm2') {
    const flat = collectWorkReportFlatSpec(row);
    for (const key of ['workBeyondScopeZakopanyM2', 'restorationZakopanyM2'] as const) {
      const v = Number(flat[key]);
      if (Number.isFinite(v) && v > 0) return v;
    }
    const removalQty = Number(flat['removalBeyondScopeQuantity']);
    if (Number.isFinite(removalQty) && removalQty > 0) return removalQty;
    if (isObnovaWorkReportActivityName(row.activityName ?? '')) {
      const obnovaM2 = resolveWorkReportObnovaM2(workReportLineItemDetailInput(row));
      if (obnovaM2 != null && obnovaM2 > 0) return obnovaM2;
    }
  }
  if (unit === 'ks') {
    return 1;
  }
  return null;
}

function workReportRowDisplayQuantity(row: WorkReportRow, item: WorkReportPriceListItem): number {
  const physical = workReportRowPhysicalQuantity(row, item.unit);
  if (physical != null && physical > 0) return roundWorkReportQuantity(physical);
  return workReportRowQuantityFromPrice(row, item.pricePerUnit);
}

function formatWorkReportQuantityCs(n: number): string {
  const rounded = roundWorkReportQuantity(n);
  if (Number.isInteger(rounded)) {
    return new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(rounded);
  }
  return new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 3 }).format(rounded);
}

function formatWorkReportQuantityUnitLabel(unit: PriceListUnit, quantity: number): string {
  const q = formatWorkReportQuantityCs(quantity);
  switch (unit) {
    case 'm':
      return `${q} m`;
    case 'ks':
      return `${q} ks`;
    case 'm2':
      return `${q} m²`;
    case 'm3':
      return `${q} m³`;
    case 'h':
      return `${q} hod`;
    case '1000_kg':
      return `${q} t`;
    default:
      return q;
  }
}

/** Stable key for netting ledger rows (matches backend work-report-adjustment.util). */
export function workReportRowLineKey(row: WorkReportRow): string {
  const meta =
    row.metadata && typeof row.metadata === 'object'
      ? (row.metadata as Record<string, unknown>)
      : {};
  const code = String(meta['listItemCode'] ?? '').trim();
  if (code) {
    const mat = (
      row.excavationMaterial ?? String(meta['excavationMaterial'] ?? '')
    ).trim();
    const sub = (
      row.excavationMaterialSubtype ?? String(meta['excavationMaterialSubtype'] ?? '')
    ).trim();
    return [code, mat, sub].filter(Boolean).join('|');
  }
  const act = (row.activityName ?? '').trim();
  const mat = (row.excavationMaterial ?? '').trim();
  const sub = (row.excavationMaterialSubtype ?? '').trim();
  return act ? `legacy:${act}|${mat}|${sub}` : `id:${row.id}`;
}

function workReportRowLedgerQuantity(row: WorkReportRow): number | null {
  const meta = row.metadata;
  if (!meta || typeof meta !== 'object') return null;
  const q = Number((meta as Record<string, unknown>)['quantity']);
  return Number.isFinite(q) ? q : null;
}

/** Součet množství pro agregovaný řádek — netování podle line key (metadata.quantity). */
export function buildWorkReportAggregatedQuantityLabel(rows: WorkReportRow[]): string | null {
  if (rows.length === 0) return null;
  const item = getPriceListItemForActivity(workReportActivityCatalogName(rows[0]!));
  if (!item) return null;

  const byLineKey = new Map<string, WorkReportRow[]>();
  for (const row of rows) {
    const key = workReportRowLineKey(row);
    const group = byLineKey.get(key);
    if (group) group.push(row);
    else byLineKey.set(key, [row]);
  }

  let sum = 0;
  for (const group of byLineKey.values()) {
    const hasLedgerQty = group.some((r) => workReportRowLedgerQuantity(r) != null);
    if (hasLedgerQty) {
      for (const row of group) {
        sum += workReportRowLedgerQuantity(row) ?? 0;
      }
    } else {
      for (const row of group) {
        sum += workReportRowDisplayQuantity(row, item);
      }
    }
  }

  sum = roundWorkReportQuantity(sum);
  if (sum <= 0) return null;
  return formatWorkReportQuantityUnitLabel(item.unit, sum);
}

function buildSameActivityAggregateLine(
  rows: WorkReportRow[],
  priceView: WorkReportMoneyView,
): WorkReportDisplayLine {
  const sorted = sortRowsByCreatedAt(rows);
  let totalPrice = 0;
  for (const r of sorted) {
    totalPrice += workReportLineAmount(r.price, r.metadata, priceView);
  }
  const titleActivity = workReportActivityCatalogName(sorted[0]!) || '—';
  const quantityLabel = buildWorkReportAggregatedQuantityLabel(sorted);

  return {
    displayId: `aggr-act-${sorted.map((r) => r.id).join('|')}`,
    isAggregate: true,
    aggregateKind: 'activity_group',
    sourceRows: sorted,
    title: titleActivity,
    quantityLabel: quantityLabel ?? undefined,
    detailLines: [],
    totalPrice,
    dateLabel: formatCsDateRange(sorted),
    phaseToRep: sorted[0]?.phaseTo ?? '',
  };
}

function buildSingleDisplayLine(row: WorkReportRow, priceView: WorkReportMoneyView): WorkReportDisplayLine {
  return {
    displayId: row.id,
    isAggregate: false,
    aggregateKind: null,
    sourceRows: [row],
    title: resolveWorkReportActivityDisplayName(row),
    detailLines: [],
    totalPrice: workReportLineAmount(row.price, row.metadata, priceView),
    dateLabel: '',
    phaseToRep: row.phaseTo,
  };
}

export function workReportDisplayLineCommonObjectId(line: WorkReportDisplayLine): string | undefined {
  const rows = line.sourceRows;
  const id = rows[0]?.objectId;
  if (!id) return undefined;
  return rows.every((r) => r.objectId === id) ? id : undefined;
}

/** `pathAdvanceBatchId` z API nebo z metadat řádku (zpětná kompatibilita). */
export function resolveWorkReportPathAdvanceBatchId(row: WorkReportRow): string | undefined {
  const direct = (row.pathAdvanceBatchId ?? '').trim();
  if (direct) return direct;
  const meta = row.metadata;
  if (meta && typeof meta === 'object') {
    const fromMeta = String((meta as Record<string, unknown>)['pathAdvanceBatchId'] ?? '').trim();
    if (fromMeta) return fromMeta;
  }
  return undefined;
}

/** Společná dávka trasy pro display line (jen pokud všechny zdrojové řádky sdílí stejné batch ID). */
export function workReportDisplayLineBatchId(line: WorkReportDisplayLine): string | undefined {
  const ids = new Set<string>();
  for (const r of line.sourceRows) {
    const bid = resolveWorkReportPathAdvanceBatchId(r);
    if (bid) ids.add(bid);
  }
  if (ids.size !== 1) return undefined;
  return [...ids][0];
}

/** CSS třídy pro zvýraznění řádků patřících do dávky trasy. */
export function buildWorkReportBatchRowClassFn(
  emphasizedBatchId: string | null | undefined,
): (line: WorkReportDisplayLine) => string {
  return (line) => {
    const bid = workReportDisplayLineBatchId(line);
    if (!bid) return '';
    if (emphasizedBatchId && bid === emphasizedBatchId) {
      return 'bg-primary-100/80 dark:bg-primary-900/40';
    }
    return 'bg-primary-50/50 dark:bg-primary-900/20';
  };
}

export function workReportDisplayLineBatchIdOnEnter(line: WorkReportDisplayLine): string | null {
  return workReportDisplayLineBatchId(line) ?? null;
}

export function flattenWorkReportDisplayLineIds(lines: WorkReportDisplayLine[]): string[] {
  const ids: string[] = [];
  for (const line of lines) {
    for (const r of line.sourceRows) ids.push(r.id);
  }
  return ids;
}

/**
 * Bulk PATCH proplacení: pokud je vybrán alespoň jeden řádek z vícerádkové skupiny (agregát),
 * rozšíří výběr na všechny řádky skupiny — vyfakturování smíšeného agregátu přenese celý řádek.
 */
export function expandWorkReportPaymentIdsForBulkPatch(selectedIds: Iterable<string>, lines: WorkReportDisplayLine[]): string[] {
  const sel = new Set(selectedIds);
  for (const line of lines) {
    if (line.sourceRows.length <= 1) continue;
    const rowIds = line.sourceRows.map((r) => r.id);
    if (rowIds.some((id) => sel.has(id))) rowIds.forEach((id) => sel.add(id));
  }
  return [...sel];
}

/** Jednotný stav proplacení pro celý souhrn, nebo „mixed“. */
export function workReportDisplayLinePaymentUniform(
  line: WorkReportDisplayLine,
): WorkReportPaymentStatus | 'mixed' {
  const rows = line.sourceRows;
  if (rows.length === 0) return WORK_REPORT_PAYMENT_STATUS.UNPAID;
  let first: ReturnType<typeof normalizeWorkReportPaymentStatus> | null = null;
  for (const r of rows) {
    const s = normalizeWorkReportPaymentStatus(r.paymentStatus);
    if (first == null) first = s;
    else if (first !== s) return 'mixed';
  }
  return first ?? WORK_REPORT_PAYMENT_STATUS.UNPAID;
}

export function workReportAggregatePaymentBadgeLabel(
  uniform: ReturnType<typeof workReportDisplayLinePaymentUniform>,
): string {
  if (uniform === 'mixed') return 'Částečně';
  return WORK_REPORT_PAYMENT_STATUS_LABELS[uniform];
}

export function workReportAggregatePaymentBadgeClass(uniform: ReturnType<typeof workReportDisplayLinePaymentUniform>): string {
  if (uniform === 'mixed') {
    return 'tt-work-report-pay-badge tt-work-report-pay-badge--unpaid';
  }
  return workReportRowPaymentBadgeClass(uniform);
}

/** Label pro jeden řádek výkazu (ne agregovaný souhrn). */
export function workReportRowPaymentLabel(paymentStatus?: string | null): string {
  const s = normalizeWorkReportPaymentStatus(paymentStatus);
  return WORK_REPORT_PAYMENT_STATUS_LABELS[s];
}

/** CSS třídy pro badge proplacení jednoho řádku. */
export function workReportRowPaymentBadgeClass(paymentStatus?: string | null): string {
  const s = normalizeWorkReportPaymentStatus(paymentStatus);
  if (s === WORK_REPORT_PAYMENT_STATUS.PAID) {
    return 'tt-work-report-pay-badge tt-work-report-pay-badge--paid';
  }
  if (s === WORK_REPORT_PAYMENT_STATUS.INVOICED) {
    return 'tt-work-report-pay-badge tt-work-report-pay-badge--invoiced';
  }
  return 'tt-work-report-pay-badge tt-work-report-pay-badge--unpaid';
}

function sortRowsByCreatedAt(rows: WorkReportRow[]): WorkReportRow[] {
  return [...rows].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

function formatCsDateRange(rows: WorkReportRow[]): string {
  const ts = rows
    .map((r) => new Date(r.createdAt).getTime())
    .filter((t) => Number.isFinite(t));
  if (ts.length === 0) return '—';
  const fmt = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
  const min = new Date(Math.min(...ts));
  const max = new Date(Math.max(...ts));
  if (min.getTime() === max.getTime()) return fmt.format(min);
  return `${fmt.format(min)} – ${fmt.format(max)}`;
}
